/* @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';

import type { PremiumProductCollections_Partner as Partner } from '__generated__/graphql-types-do-not-use';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { processImage } from 'js/lib/imgix';

import { Grid, breakpoints } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import type { Maybe } from 'bundles/premium-certificate/__generated__/baseContentfulTypes';
import type {
  EarnCreditFragment,
  MastertrackPartnerInformationFragment,
} from 'bundles/premium-certificate/__generated__/contentfulTypes';
import { ARROW_LINE_MOBILE } from 'bundles/premium-certificate/constants';

import _t from 'i18n!nls/premium-certificate';

type Props = EarnCreditFragment & {
  partnerInformation?: MastertrackPartnerInformationFragment | null;
  certificateName?: Maybe<string>;
  certificateType?: Maybe<string>;
  certificateFullName?: string;
  partnerCatalogData?: Maybe<Partner>;
};

const styles: Record<string, Interpolation<Theme>> = {
  earnCreditTitle: () => css`
    font-family: inherit;
    color: var(--cds-color-neutral-primary);
    font-weight: 700;
    margin-bottom: 1.5rem;
    text-align: center;
    font-size: 1.5rem;
    line-height: 1.875rem;

    @media (max-width: ${breakpoints.values.md}px) {
      text-align: left;
    }
  `,
  earnCreditDescription: () => css`
    .rc-Markdown,
    span {
      color: var(--cds-color-neutral-primary);
      line-height: 1.5rem;
      text-align: center;
      display: block;

      @media (max-width: ${breakpoints.values.sm}px) {
        text-align: left;
        font-size: 0.875rem;
      }
    }
  `,
  mastertrackInfoContainer: css`
    margin: 1rem;
    padding: 2.25rem;
    border: 1px solid var(--cds-color-grey-25);
    display: flex;
    flex-direction: column;
    align-items: center;
  `,
  partnerLogoContainer: css`
    margin-bottom: 1rem;
  `,
  partnerLogo: css`
    max-height: 40px;
  `,
  certificateType: () => css`
    font-size: 0.875rem;
    line-height: 1.5rem;
    color: var(--cds-color-neutral-primary);
    text-align: center;
  `,
  certificateName: () => css`
    font-size: 1.25rem;
    line-height: 1.5rem;
    color: var(--cds-color-neutral-primary);
    text-align: center;
  `,
  stackableDestinationContainer: css`
    padding: 2rem;
    background-color: var(--cds-color-neutral-background-primary-weak);
    border-radius: 4px;
    box-shadow: 0 3px 6px var(--cds-color-grey-400);
  `,
  stackableDestinationTitle: () => css`
    font-family: inherit;
    color: var(--cds-color-neutral-primary);
    font-size: 1rem;
    line-height: 1.5rem;
  `,
  stackableDestination: () => css`
    background-color: var(--cds-color-neutral-background-primary);
    width: 100%;
    padding: 0.5rem;
    font-size: 0.75rem;
    line-height: 1.125rem;
    display: flex;
    align-items: center;
    border-radius: 5px;
    box-shadow: 0 2px 6px var(--cds-color-grey-400);
    margin-bottom: 1.25rem;
  `,
  partnerSquareLogoContainer: css`
    margin-right: 0.5rem;
  `,
  partnerSquareLogo: css`
    height: 36px;
    width: 36px;
    border-radius: 5px;
    border: 1px solid var(--cds-color-grey-200);
  `,
  stackableDestinationLink: css`
    font-size: 0.875rem;
    display: inline;
  `,
  earnCreditFinePrint: css`
    font-size: 0.875rem;
    line-height: 1.125rem;
    color: var(--cds-color-grey-850);
  `,
  connectionLineContainer: css`
    position: absolute;
    left: 43%;
    top: 0;
    width: 60%;
    height: 100%;
    background-size: 100% 100%;
    background-repeat: no-repeat;
  `,
  mastertrackInfoCol: () => css`
    background-color: var(--cds-color-neutral-background-primary);
    max-width: 480px;
  `,
  stackableDestinationCol: css`
    max-width: 480px;
  `,
  earnCreditCol: css`
    max-width: 500px;
    padding-top: 4.5rem;
    padding-bottom: 4.5rem;
    position: relative;
  `,
};

const EarnCreditMobile = ({
  earnCreditTitle,
  earnCreditDescription,
  earnCreditTowardsText,
  earnCreditFinePrint,
  stackableDestinationName,
  stackableDestinationUrl,
  isStackableDestination100Online,
  certificateName,
  certificateType,
  certificateFullName,
  partnerInformation,
  partnerCatalogData,
}: Props) => (
  <div data-test="EarnCreditMobile">
    <Grid container justifyContent="center">
      <Grid item css={styles.mastertrackInfoCol}>
        <div css={styles.mastertrackInfoContainer}>
          {partnerInformation?.partnerMonotoneLogo?.url && (
            <div css={styles.partnerLogoContainer} data-test="partnerLogoContainer">
              <img
                css={styles.partnerLogo}
                src={partnerInformation.partnerMonotoneLogo.url}
                alt={partnerInformation.partnerName || _t('Partner Logo')}
              />
            </div>
          )}
          <p css={styles.certificateType}>{certificateType}</p>
          <p css={styles.certificateName}>{certificateName}</p>
        </div>
      </Grid>
      <Grid item css={styles.earnCreditCol}>
        <div
          css={styles.connectionLineContainer}
          style={{
            backgroundImage: `url(${processImage(ARROW_LINE_MOBILE)})`,
          }}
        />
        <h2 css={styles.earnCreditTitle} data-test="earn-credit">
          {earnCreditTitle ||
            _t(`Earn credit toward a 100% online master's degree from #{partnerName}`, {
              partnerName: partnerInformation?.partnerName,
            })}
        </h2>
        <div css={styles.earnCreditDescription}>
          {earnCreditDescription ? (
            <Markdown source={earnCreditDescription} />
          ) : (
            <FormattedMessage
              message={_t(`Take the next step in your education to boost your career. This {certificate} is a building
block that offers you a pathway to a degree while also providing job-relevant skills today.`)}
              certificate={<strong>{_t('MasterTrack® Certificate')}</strong>}
            />
          )}
        </div>
      </Grid>
      <Grid item css={styles.stackableDestinationCol}>
        <div css={styles.stackableDestinationContainer}>
          <p css={styles.stackableDestinationTitle}>
            {certificateFullName} <strong>{`${earnCreditTowardsText || _t('earn credit towards')}:`}</strong>
          </p>
          {stackableDestinationUrl && (
            <div css={styles.stackableDestination} data-test="stackable-destination">
              {partnerCatalogData?.logo && (
                <div css={styles.partnerSquareLogoContainer} data-test="partner-square-logo-container">
                  <img css={styles.partnerSquareLogo} src={partnerCatalogData.logo} alt={partnerCatalogData.name} />
                </div>
              )}
              <div>
                <a href={stackableDestinationUrl} css={styles.stackableDestinationLink}>
                  {stackableDestinationName}
                </a>
                {isStackableDestination100Online && <span data-test="is-stackable"> ({_t('100% online')})</span>}
              </div>
            </div>
          )}
          {earnCreditFinePrint && (
            <div css={styles.earnCreditFinePrint} data-test="earn-credit-fine-print">
              {earnCreditFinePrint}
            </div>
          )}
        </div>
      </Grid>
    </Grid>
  </div>
);

export default EarnCreditMobile;
